import axios from 'axios';
import {defineStore} from 'pinia';
import {computed, type ComputedRef, type Ref, ref} from 'vue';

import {baseURL} from '@/shared/constants';
import {UserRoles} from '@/shared/enums/userRolesEnum';
import {AuthApi} from '@/shared/features/auth/auth.api';
import type {AuthToken} from '@/shared/features/auth/auth.model';
import type {UserModel} from '@/shared/features/users/user.model';

interface IDefaultUser {
   id?: number;
   name: string;
   role: UserRoles | null;
   belongsToSchool: boolean;
   hasTrial: boolean;
   isConnectedWithClever: boolean;
}

const defaultUser: IDefaultUser = {
   name: '',
   role: null,
   belongsToSchool: false,
   hasTrial: true,
   isConnectedWithClever: false,
};

const USER_STORAGE_KEY = 'user_data';
const USER_TOKEN_STORAGE_KEY = 'u_token';

export const useUserStore = defineStore('user', () => {
   const getInitialState = (): UserModel | IDefaultUser => {
      const storedUser = localStorage.getItem(USER_STORAGE_KEY);
      if (storedUser) {
         try {
            return JSON.parse(storedUser) as UserModel;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
         } catch (error) {}
      }
      return defaultUser;
   };

   const user: Ref<UserModel | IDefaultUser> = ref(getInitialState());
   const token: Ref<AuthToken | null> = ref(
      JSON.parse(localStorage.getItem(USER_TOKEN_STORAGE_KEY) || 'null'),
   );

   const isAuth = computed(() => !!token.value);
   const getToken: ComputedRef<AuthToken|null> = computed(() => token.value);
   const getUser = computed(() => user.value);

   const setUser = (userData: UserModel) => {
      user.value = userData;
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
   };

   const setToken = (userToken: AuthToken | null) => {
      token.value = userToken;
      if (userToken) {
         AuthApi.setAuthHeader(userToken.access_token);
         localStorage.setItem(USER_TOKEN_STORAGE_KEY, JSON.stringify(userToken));
      }
   };

   const refreshToken = async () => {
      if (!token.value?.refresh_token) {
         // const {logout} = useAuth();
         // logout(true).then();
         return Promise.reject('No refresh token');
      }

      try {
         const response = await axios.post(`${baseURL}/auth/refresh`, {
            refresh_token: token.value.refresh_token,
         });

         setToken(response.data);
         return response.data.access_token;
      } catch (error) {
         // const {logout} = useAuth();
         // logout(true).then();
         return Promise.reject(error);
      }
   };

   const clearUser = () => {
      localStorage.removeItem(USER_STORAGE_KEY);
      localStorage.removeItem(USER_TOKEN_STORAGE_KEY);
      user.value = defaultUser;
      token.value = null;
      AuthApi.clearAuthHeader();
   };

   const deleteToken = () => {
      token.value = null;
      localStorage.removeItem(USER_TOKEN_STORAGE_KEY);
      AuthApi.clearAuthHeader();
   };

   const isStudentAuth = (): boolean => {
      if (!isAuth.value) return false;
      if (user.value.role) {
         return user.value.role === UserRoles.STUDENT;
      }
      return false;
   };

   const isDashboardAuth = (): boolean => {
      if (!isAuth.value) return false;
      if (user.value.role) {
         return [
            UserRoles.IT_ADMIN,
            UserRoles.TENANT_ADMIN,
            UserRoles.DISTRICT_ADMIN,
            UserRoles.SCHOOL_ADMIN,
            UserRoles.TEACHER,
         ].includes(user.value.role);
      }
      return false;
   };

   return {
      user,
      token,
      isAuth,
      isStudentAuth,
      isDashboardAuth,
      getToken,
      getUser,
      setUser,
      setToken,
      clearUser,
      deleteToken,
      refreshToken,
   };
});
